import { ApiHttpError, apiFetch, apiJson } from "../../lib/api";
import type {
  ActivationState, CandidateVersion, DataDraft, DataHistoryItem, DataManagementState, DraftValidation,
  ImportSession, ImportSessionLimits, JobHistoryItem, MaterializationJob, MaterializationState, Page, ProcessingJob,
  SourceRevision, SourcesResponse, VersionPublication,
  SourceExplorerFilesPage, SourceExplorerMetadata, SourceExplorerTree, SourceFilePreview,
} from "./types";

export type DatasetLocation = { clientId: string; datasetId: string };

export const dataKeys = {
  root: (value: DatasetLocation) => ["data-management", value.clientId, value.datasetId] as const,
  context: (value: DatasetLocation) => [...dataKeys.root(value), "context"] as const,
  sources: (value: DatasetLocation) => [...dataKeys.root(value), "sources"] as const,
  revisions: (value: DatasetLocation) => [...dataKeys.root(value), "revisions"] as const,
  validation: (value: DatasetLocation) => [...dataKeys.root(value), "validation"] as const,
  jobs: (value: DatasetLocation) => [...dataKeys.root(value), "jobs"] as const,
  versions: (value: DatasetLocation) => [...dataKeys.root(value), "versions"] as const,
  publication: (value: DatasetLocation, versionId: string) => [...dataKeys.root(value), "publication", versionId] as const,
  activation: (value: DatasetLocation) => [...dataKeys.root(value), "activation"] as const,
  materialization: (value: DatasetLocation) => [...dataKeys.root(value), "materialization"] as const,
  history: (value: DatasetLocation) => [...dataKeys.root(value), "history"] as const,
  explorerTree: (value: DatasetLocation, revisionId: string) => [...dataKeys.root(value), "explorer", revisionId, "tree"] as const,
  explorerFiles: (value: DatasetLocation, revisionId: string, query: string) => [...dataKeys.root(value), "explorer", revisionId, "files", query] as const,
  explorerMetadata: (value: DatasetLocation, revisionId: string, fileId: string) => [...dataKeys.root(value), "explorer", revisionId, "metadata", fileId] as const,
  explorerPreview: (value: DatasetLocation, revisionId: string, fileId: string, sheet: string) => [...dataKeys.root(value), "explorer", revisionId, "preview", fileId, sheet] as const,
};

export async function getContext(value: DatasetLocation) {
  return await apiJson<DataManagementState>(path(value, "data-management"));
}
export async function initializeDataManagement(value: DatasetLocation) {
  return await apiJson<Extract<DataManagementState, { state: "initialized" }> & { created: boolean }>(
    path(value, "data-management/initialize"),
    { method: "POST" },
  );
}
export async function openDataDraft(value: DatasetLocation) {
  return await apiJson<{ draft: DataDraft; created: boolean }>(
    path(value, "draft/open"),
    { method: "POST" },
  );
}
export async function getSources(value: DatasetLocation) {
  return await apiJson<SourcesResponse>(path(value, "draft/sources"));
}
export async function uploadSources(
  value: DatasetLocation,
  files: File[],
  signal: AbortSignal,
  role = "primary",
  sourceRevision?: number,
  replacementOfSourceId?: string,
) {
  const sources: SourcesResponse["sources"] = [];
  let expectedRevision = sourceRevision;
  for (const file of files) {
    const body = new FormData();
    const relativePath = file.webkitRelativePath || file.name;
    body.append("files", file, relativePath);
    const headers: Record<string, string> = {
      "Idempotency-Key": crypto.randomUUID(),
      "X-Source-Role": role,
    };
    if (expectedRevision) headers["X-Expected-Source-Revision"] = `${expectedRevision}`;
    if (replacementOfSourceId) headers["X-Replaces-Source-Id"] = replacementOfSourceId;
    const response = await apiFetch(path(value, "draft/sources/upload"), { method: "POST", body, signal, headers });
    const payload = await response.json().catch(() => null) as { sources: SourcesResponse["sources"] } | null;
    if (!response.ok) throw new ApiHttpError(response.status, payload);
    sources.push(...(payload?.sources ?? []));
    if (expectedRevision) expectedRevision += 1;
  }
  return { sources };
}

export async function createImportSession(
  value: DatasetLocation,
  input: {
    expectedRevision: number;
    role: string;
    mode: "files" | "folder" | "zip";
    expectedFiles: number;
    expectedBytes: number;
    idempotencyKey: string;
  },
) {
  return await apiJson<{ session: ImportSession; limits: ImportSessionLimits }>(
    path(value, "draft/import-sessions"),
    {
      method: "POST",
      headers: { "Idempotency-Key": input.idempotencyKey },
      body: JSON.stringify({
        expectedRevision: input.expectedRevision,
        role: input.role,
        mode: input.mode,
        expectedFiles: input.expectedFiles,
        expectedBytes: input.expectedBytes,
      }),
    },
  );
}

export async function getActiveImportSession(value: DatasetLocation) {
  return (await apiJson<{ session: ImportSession | null }>(
    path(value, "draft/import-sessions/active"),
  )).session;
}

export async function getImportSession(value: DatasetLocation, sessionId: string) {
  return (await apiJson<{ session: ImportSession }>(
    path(value, `draft/import-sessions/${encodeURIComponent(sessionId)}`),
  )).session;
}

export async function uploadImportFile(
  value: DatasetLocation,
  sessionId: string,
  file: File,
  index: number,
  signal: AbortSignal,
) {
  const relativePath = file.webkitRelativePath || file.name;
  const body = new FormData();
  body.append("file", file, file.name);
  const response = await apiFetch(
    path(value, `draft/import-sessions/${encodeURIComponent(sessionId)}/files`),
    {
      method: "POST",
      body,
      signal,
      headers: {
        "Idempotency-Key": `file:${sessionId}:${index}`,
        "X-File-Index": `${index}`,
        "X-File-Size": `${file.size}`,
        "X-Relative-Path": encodeURIComponent(relativePath),
        "X-Original-File-Name": encodeURIComponent(file.name),
        "X-File-Media-Type": file.type || "application/octet-stream",
      },
    },
  );
  const payload = await response.json().catch(() => null) as { session: ImportSession } | null;
  if (!response.ok) throw new ApiHttpError(response.status, payload);
  if (!payload) throw new ApiHttpError(response.status, null);
  return payload.session;
}

export async function finalizeImportSession(value: DatasetLocation, sessionId: string) {
  return (await apiJson<{ session: ImportSession }>(
    path(value, `draft/import-sessions/${encodeURIComponent(sessionId)}/finalize`),
    { method: "POST", body: JSON.stringify({}) },
  )).session;
}

export async function cancelImportSession(value: DatasetLocation, sessionId: string) {
  return (await apiJson<{ session: ImportSession }>(
    path(value, `draft/import-sessions/${encodeURIComponent(sessionId)}/cancel`),
    { method: "POST", body: JSON.stringify({}) },
  )).session;
}

export async function moveSource(value: DatasetLocation, sourceId: string, role: string, expectedRevision: number) {
  return await apiJson(path(value, `draft/sources/${encodeURIComponent(sourceId)}/move`), {
    method: "POST",
    headers: { "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify({ role, expectedRevision }),
  });
}

export async function removeSource(value: DatasetLocation, sourceId: string, expectedRevision: number) {
  return await apiJson(path(value, `draft/sources/${encodeURIComponent(sourceId)}`), {
    method: "DELETE",
    headers: {
      "Idempotency-Key": crypto.randomUUID(),
      "X-Expected-Source-Revision": `${expectedRevision}`,
    },
  });
}

export async function changeSourceLogicalPath(
  value: DatasetLocation,
  sourceId: string,
  virtualPath: string,
  expectedRevision: number,
) {
  return await apiJson<{ sourceRevision: number }>(
    path(value, `draft/sources/${encodeURIComponent(sourceId)}/logical-path`),
    {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ virtualPath, expectedRevision }),
    },
  );
}

export async function createDraftFromRevision(value: DatasetLocation, revisionId: string) {
  return await apiJson<{ draft: DataDraft; revisionId: string; baseRevision: number; created: boolean }>(
    sourceRevisionPath(value, revisionId, "new-draft"),
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function sourceDownloadUrl(value: DatasetLocation, sourceId: string) {
  return path(value, `draft/sources/${encodeURIComponent(sourceId)}/download`);
}
export async function getRevisions(value: DatasetLocation) {
  return (await apiJson<{ revisions: SourceRevision[] }>(path(value, "source-revisions?limit=50&includeFiles=false"))).revisions;
}

export async function getSourceExplorerTree(value: DatasetLocation, revisionId: string) {
  return await apiJson<SourceExplorerTree>(sourceRevisionPath(value, revisionId, "tree"));
}

export async function getSourceExplorerFiles(
  value: DatasetLocation,
  revisionId: string,
  input: { path: string; search: string; type: string; role: string; sort: string; order: string; offset: number; limit: number },
) {
  const query = new URLSearchParams();
  if (input.path) query.set("path", input.path);
  if (input.search) query.set("search", input.search);
  if (input.type) query.set("type", input.type);
  if (input.role) query.set("role", input.role);
  query.set("sort", input.sort);
  query.set("order", input.order);
  query.set("offset", `${input.offset}`);
  query.set("limit", `${input.limit}`);
  return await apiJson<SourceExplorerFilesPage>(sourceRevisionPath(value, revisionId, `files?${query}`));
}

export async function getSourceExplorerMetadata(value: DatasetLocation, revisionId: string, fileId: string, signal?: AbortSignal) {
  return (await apiJson<{ file: SourceExplorerMetadata }>(
    sourceRevisionPath(value, revisionId, `files/${encodeURIComponent(fileId)}`),
    { signal },
  )).file;
}

export async function getSourceExplorerPreview(
  value: DatasetLocation,
  revisionId: string,
  fileId: string,
  sheet = "",
  signal?: AbortSignal,
) {
  const suffix = sheet ? `?sheet=${encodeURIComponent(sheet)}` : "";
  return await apiJson<SourceFilePreview>(
    sourceRevisionPath(value, revisionId, `files/${encodeURIComponent(fileId)}/preview${suffix}`),
    { signal },
  );
}

export async function getSourceExplorerContent(
  value: DatasetLocation,
  revisionId: string,
  fileId: string,
  disposition: "inline" | "attachment",
  signal?: AbortSignal,
) {
  const response = await apiFetch(sourceRevisionPath(
    value,
    revisionId,
    `files/${encodeURIComponent(fileId)}/content?disposition=${disposition}`,
  ), { signal });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiHttpError(response.status, payload);
  }
  return await response.blob();
}
export async function getValidation(value: DatasetLocation) {
  try {
    return (await apiJson<{ validation: DraftValidation }>(path(value, "draft/validation"))).validation;
  } catch (error) {
    if (error instanceof ApiHttpError && error.status === 404) return null;
    throw error;
  }
}
export async function launchProcessing(value: DatasetLocation, action: "validate" | "semanticize") {
  return await apiJson<{ job: ProcessingJob; validation: DraftValidation | null; version: CandidateVersion | null; created: boolean }>(
    path(value, `draft/${action}`), { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
}
export async function getJobs(value: DatasetLocation) {
  return await apiJson<Page<JobHistoryItem>>(path(value, "jobs?limit=20"));
}
export async function cancelProcessingJob(value: DatasetLocation, jobId: string) {
  return (await apiJson<{ job: ProcessingJob }>(path(value, `jobs/${encodeURIComponent(jobId)}/cancel`), {
    method: "POST",
    body: JSON.stringify({}),
  })).job;
}
export async function getVersions(value: DatasetLocation) {
  return (await apiJson<{ versions: CandidateVersion[] }>(path(value, "versions?limit=50"))).versions;
}
export async function getPublication(value: DatasetLocation, versionId: string) {
  return (await apiJson<{ publication: VersionPublication }>(path(value, `versions/${encodeURIComponent(versionId)}/publication`))).publication;
}
export async function publishVersion(value: DatasetLocation, versionId: string) {
  return await apiJson<{ publication: VersionPublication; created: boolean }>(
    path(value, `versions/${encodeURIComponent(versionId)}/publish`),
    { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
}
export async function getActivation(value: DatasetLocation) {
  return (await apiJson<{ activation: ActivationState }>(path(value, "activation"))).activation;
}
export async function activateVersion(value: DatasetLocation, versionId: string) {
  return await apiJson<{ outcome: string; activation: ActivationState }>(
    path(value, `versions/${encodeURIComponent(versionId)}/activate`), { method: "POST" },
  );
}
export async function getMaterialization(value: DatasetLocation) {
  return (await apiJson<{ materialization: MaterializationState }>(path(value, "materialization"))).materialization;
}
export async function materializeActive(value: DatasetLocation) {
  return await apiJson<{ job: MaterializationJob; created: boolean; idempotent: boolean }>(
    path(value, "materialize"), { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
}
export async function getHistory(value: DatasetLocation) {
  return await apiJson<Page<DataHistoryItem>>(path(value, "history?limit=20"));
}

export function safeErrorMessage(error: unknown) {
  if (!(error instanceof ApiHttpError)) return "Une erreur inattendue est survenue.";
  if (!error.payload || typeof error.payload !== "object") return error.message;
  const payload = error.payload as { error?: { message?: unknown } };
  if (typeof payload.error?.message === "string") return payload.error.message;
  return error.message;
}

function path(value: DatasetLocation, suffix: string) {
  return `/api/clients/${encodeURIComponent(value.clientId)}/datasets/${encodeURIComponent(value.datasetId)}/${suffix}`;
}

function sourceRevisionPath(value: DatasetLocation, revisionId: string, suffix: string) {
  return path(value, `source-revisions/${encodeURIComponent(revisionId)}/${suffix}`);
}
